import { createContext, useContext, useState, useEffect } from 'react';
import useApi from '../../shared/hooks/useApi';

export const SimulationContext = createContext();

const PENDING_SIMULATIONS_KEY = 'esid-25-006-pending-simulations';
const PENDING_SIMULATION_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds

export const SimulationProvider = ({ children }) => {
    const { apiGet, apiPost } = useApi();
    const [simulations, setSimulations] = useState([]);
    const [runningSimulations, setRunningSimulations] = useState([]);
    const [allSimulationsStatus, setAllSimulationsStatus] = useState([]);
    const [pendingSimulations, setPendingSimulations] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [startDate, setStartDate] = useState(new Date(new Date().setMonth(new Date().getMonth() - 2)).toISOString().split('T')[0]);//2 months before now
    const [endDate, setEndDate] = useState(new Date(new Date().setHours(new Date().getHours())).toISOString().split('T')[0]);//now
    // Load pending simulations from localStorage
    const loadPendingSimulations = () => {
        try {
            const stored = localStorage.getItem(PENDING_SIMULATIONS_KEY);
            if (stored) {
                const pending = JSON.parse(stored);
                const now = Date.now();
                
                // Filter out simulations older than 30 minutes
                const validPending = pending.filter(sim => {
                    const age = now - sim.timestamp;
                    return age < PENDING_SIMULATION_TIMEOUT;
                });

                // Update localStorage if any were filtered out
                if (validPending.length !== pending.length) {
                    localStorage.setItem(PENDING_SIMULATIONS_KEY, JSON.stringify(validPending));
                }

                setPendingSimulations(validPending);
                return validPending;
            }
        } catch (error) {
            console.error("Error loading pending simulations:", error);
        }
        return [];
    };

    // Add a pending simulation
    const addPendingSimulation = (simulation) => {
        try {
            const pending = loadPendingSimulations();
            const updated = [...pending, simulation];
            localStorage.setItem(PENDING_SIMULATIONS_KEY, JSON.stringify(updated));
            setPendingSimulations(updated);
        } catch (error) {
            console.error("Error adding pending simulation:", error);
        }
    };

    // Clean up old pending simulations
    const cleanupPendingSimulations = () => {
        const validPending = loadPendingSimulations();
        setPendingSimulations(validPending);
    };

    // Fetch all simulations from API
    const fetchSimulations = async (loading = true) => {
        if (loading) {
            setIsLoading(true);
        }
        setError(null);

        try {
            const response = await apiGet("UX_HTTP_API", "/esid-25-006/fetch-all-simulations");
            if (response && response.simulations) {
                const { simulations } = response;

                // Sort simulations by created date (newest first)
                const sortedSimulations = simulations.sort((a, b) => {
                    const dateA = new Date(a.created);
                    const dateB = new Date(b.created);
                    return dateB - dateA; // Descending order (newest first)
                });

                setSimulations(sortedSimulations);
                
                // Clean up pending simulations that now have real results
                const pending = loadPendingSimulations();
                const simulationNames = sortedSimulations.map(s => {
                    try {
                        const msg = JSON.parse(s.message);
                        return msg.name;
                    } catch {
                        return null;
                    }
                });
                
                const stillPending = pending.filter(p => {
                    try {
                        const msg = JSON.parse(p.message);
                        return !simulationNames.includes(msg.name);
                    } catch {
                        return true;
                    }
                });

                if (stillPending.length !== pending.length) {
                    localStorage.setItem(PENDING_SIMULATIONS_KEY, JSON.stringify(stillPending));
                    setPendingSimulations(stillPending);
                }
            } else {
                console.warn("No simulations data in response:", response);
                setSimulations([]);
            }
            if (response && response.running_simulations) {
                const { running_simulations } = response;
                const sortedRunningSimulations = running_simulations.sort((a, b) => {
                    const dateA = new Date(a.created);
                    const dateB = new Date(b.created);
                    return dateB - dateA; // Descending order (newest first)
                });
                setRunningSimulations(sortedRunningSimulations);
            } else {
                console.warn("No running simulations data in response:", response);
                setRunningSimulations([]);
            }
        } catch (error) {
            console.error("Error fetching simulations:", error);
            setError(error.message || "Failed to fetch simulations");
            setSimulations([]);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchAllSimulationsStatus = async () => {
        try {
            const response = await apiPost("UX_HTTP_API", "/esid-25-006/get-all-simulations-status", {
                startDate: startDate,
                endDate: endDate,
            });
            
            if (response && response.data && response.data.simulations) {
                const apiSimulations = response.data.simulations;
                
                // Filter for active simulations (STARTED or CALCULATING)
          
                const filteredStatusesbf = apiSimulations.filter(status => 
                    status.status === "STARTED" || status.status === "CALCULATING" || status.status === "DATA-FETCHED"
                );

                // Show simulations as FAILED instead of hiding them if they started more than 1 hour ago
                const ONE_HOUR_MS = 1 * 60 * 60 * 1000;
                const now = new Date().getTime();

                const filteredStatuses = filteredStatusesbf.map(simulation => {
                    const startedAtTime = new Date(simulation.startedAt).getTime();
                    
                    if (!isNaN(startedAtTime) && startedAtTime < now - ONE_HOUR_MS) {
                        return {
                            ...simulation,
                            status: "FAILED",
                        };
                    }

                    return simulation;
                });
                // Load current pending simulations
                const pending = loadPendingSimulations();
                const MAX_TIME_DIFF = 5 * 60 * 1000; // 5 minutes in milliseconds
                
                // Find matches between pending and API simulations
                const matchedPendingIds = new Set();
                
                
                pending.forEach(pendingSim => {
                    const pendingCreated = new Date(pendingSim.created).getTime();
                    
                    apiSimulations.forEach(apiSim => {
                        const startedAt = new Date(apiSim.startedAt).getTime();
                        const updatedAt = new Date(apiSim.updatedAt).getTime();
                        
                        // Check if pending created time is between startedAt and updatedAt
                        const isBetween = pendingCreated >= startedAt && pendingCreated <= updatedAt;
                        
                        // Check if pending created time is within 5 minutes of startedAt or updatedAt
                        const distanceToStarted = Math.abs(pendingCreated - startedAt);
                        const distanceToUpdated = Math.abs(pendingCreated - updatedAt);
                        const isWithinTimeRange = distanceToStarted <= MAX_TIME_DIFF || distanceToUpdated <= MAX_TIME_DIFF;
                        
                        console.log(`Comparing pending ${pendingSim.created} with API ${apiSim.startedAt}-${apiSim.updatedAt}:`, {
                            isBetween,
                            distanceToStarted: `${(distanceToStarted / 1000).toFixed(0)}s`,
                            distanceToUpdated: `${(distanceToUpdated / 1000).toFixed(0)}s`,
                            isWithinTimeRange
                        });
                        
                        // If conditions match, mark this pending simulation for removal
                        if (isBetween || isWithinTimeRange) {
                            console.log(`✅ Matching pending simulation ${pendingSim.event_id} with API simulation ${apiSim["event-id"]}`);
                            matchedPendingIds.add(pendingSim.event_id);
                        }
                    });
                });
                
                // Remove matched pending simulations from localStorage
                if (matchedPendingIds.size > 0) {
                    const updatedPending = pending.filter(p => !matchedPendingIds.has(p.event_id));
                    localStorage.setItem(PENDING_SIMULATIONS_KEY, JSON.stringify(updatedPending));
                    setPendingSimulations(updatedPending);
                    console.log(`Removed ${matchedPendingIds.size} matched pending simulations`);
                }
                
                // Map API simulations to the expected format
                const mappedSimulations = filteredStatuses.map(status => ({
                    created: status.startedAt || status.created || new Date().toISOString(),
                    event_id: status["event-id"] || status.event_id,
                    ttl: status.ttl || "",
                    message: JSON.stringify({
                        name: "Running Simulation",
                        status: status.status,
                        s3_key: "",
                        "ux-filters": {}
                    }),
                    partition_key: "ESID-25-006:SIMULATION#RUNNING",
                    ip_address: "",
                    startedAt: status.startedAt,
                    updatedAt: status.updatedAt
                }));
                
                setAllSimulationsStatus(mappedSimulations);
            }
        } catch (error) {
            console.error("Error fetching all simulations status:", error);
            setAllSimulationsStatus([]);
        }
    };
    // Fetch simulations on mount only
    useEffect(() => {
        // Load pending simulations from localStorage on mount
        loadPendingSimulations();
        
        fetchSimulations(true);
        fetchAllSimulationsStatus();
        
        // Set up interval for fetching and cleanup
        const intervalId = setInterval(() => {
            fetchSimulations(false);
            fetchAllSimulationsStatus();
            cleanupPendingSimulations();
        }, 60000); // Every minute

        // Cleanup interval on unmount
        return () => clearInterval(intervalId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Provide context value
    const contextValue = {
        simulations,
        setSimulations,
        runningSimulations,
        setRunningSimulations,
        pendingSimulations,
        isLoading,
        error,
        fetchSimulations, // Allow manual refresh
        allSimulationsStatus,
        addPendingSimulation,
        cleanupPendingSimulations,
    };

    return (
        <SimulationContext.Provider value={contextValue}>
            {children}
        </SimulationContext.Provider>
    );
};

export const useSimulation = () => {
    const context = useContext(SimulationContext);
    if (!context) {
        throw new Error('useSimulation must be used within a SimulationProvider');
    }
    return context;
};